import { useRef, type ReactNode } from "react";
import {
  SelectContent,
  SelectItem,
  SelectPrimitiveRoot,
  SelectPrimitiveTrigger,
} from "@porchpass/ui";

interface SalesRepSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSalesRepId: string;
  onSelectEmployee: (employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }) => void;
  canModify: boolean;
  isUpdating?: boolean;
  employees: Array<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  isLoading: boolean;
  children: ReactNode;
}

export default ({
  open,
  onOpenChange,
  selectedSalesRepId,
  onSelectEmployee,
  canModify,
  isUpdating = false,
  employees,
  isLoading,
  children,
}: SalesRepSelectorProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);

    if (!nextOpen) {
      requestAnimationFrame(() => {
        triggerRef.current?.blur();
      });
    }
  };

  const headerText = canModify
    ? "Select a sales representative"
    : "You don't have permission to change the sales rep";

  return (
    <SelectPrimitiveRoot
      open={open}
      onOpenChange={handleOpenChange}
      value={selectedSalesRepId}
      disabled={!canModify}
      onValueChange={(newId) => {
        if (!canModify || isUpdating || newId === selectedSalesRepId) {
          return;
        }

        const employee = employees.find((emp) => emp.id === newId);
        if (employee) {
          onSelectEmployee(employee);
        }
      }}
    >
      <SelectPrimitiveTrigger asChild ref={triggerRef}>
        {children}
      </SelectPrimitiveTrigger>
      <SelectContent
        side="top"
        align="end"
        header={headerText}
        className="max-h-[60vh] min-w-[280px]"
      >
        {isLoading ? (
          <div className="px-3 py-3 text-md font-medium tracking-tight text-on-surface-primary">
            Loading employees...
          </div>
        ) : employees.length === 0 ? (
          <div className="px-3 py-3 text-md font-medium tracking-tight text-on-surface-primary">
            No employees found
          </div>
        ) : (
          employees.map((employee) => {
            const displayName =
              employee.first_name && employee.last_name
                ? `${employee.first_name} ${employee.last_name}`
                : employee.email;

            return (
              <SelectItem
                key={employee.id}
                value={employee.id}
                disabled={!canModify || isUpdating}
              >
                {displayName}
              </SelectItem>
            );
          })
        )}
      </SelectContent>
    </SelectPrimitiveRoot>
  );
};
